/**
 * A picture's frame, mask, soft edges and fade.
 *
 * Opened from the picture's toolbar. What it offers depends on what the picture
 * is seen through: a frame can change shape, a mask can be released back onto
 * the page, and any picture can have soft edges or fade out.
 */

import type { ImageLayer, TextLayer } from '../model/types';
import { useEditor, useSelectedLayers } from '../state/store';
import { shapeDef } from '../shapes/library';
import { FRAME_SHAPES, releaseMask, replace, setFade, setFrameShape, setMask } from '../state/masks';
import { allAssets, toDocumentRef } from '../assets';
import { Preview } from './ShapePicker';
import { Slider } from './Slider';

export function MaskPanel( { onClose }: { onClose: () => void } ) {
  const selected = useSelectedLayers();
  const layer = selected.length === 1 && selected[ 0 ]?.type === 'image' ? ( selected[ 0 ] as ImageLayer ) : null;

  return (
    <aside className="tb-side" aria-label="Mask">
      <div className="tb-side__head">
        <h2 className="tb-side__title">{ layer?.mask?.frame ? 'Frame' : 'Mask' }</h2>
        <button type="button" className="tb-btn tb-btn--quiet tb-btn--icon" aria-label="Close mask" onClick={ onClose }>
          ✕
        </button>
      </div>

      { ! layer ? (
        <p className="tb-side__empty">Select a picture to frame it, mask it or soften its edges.</p>
      ) : (
        <MaskControls layer={ layer } />
      ) }
    </aside>
  );
}

function MaskControls( { layer }: { layer: ImageLayer } ) {
  const mask = layer.mask ?? null;
  const outline = mask?.layer ?? null;
  const fade = mask?.fade ?? null;

  return (
    <div className="tb-colour-panel tb-mask">
      { mask?.frame && outline?.type === 'shape' && (
        <section className="tb-props__section">
          <h3 className="tb-props__heading">Shape</h3>
          <div className="tb-rp__shapes">
            { FRAME_SHAPES.map( ( id ) => (
              <button
                key={ id }
                type="button"
                className={ `tb-rp__shape${ outline.shape === id ? ' is-on' : '' }` }
                title={ shapeDef( id ).name }
                aria-label={ shapeDef( id ).name }
                aria-pressed={ outline.shape === id }
                data-frame-shape={ id }
                onClick={ () => setFrameShape( layer, id ) }
              >
                <Preview shape={ shapeDef( id ) } />
              </button>
            ) ) }
          </div>
          <p className="tb-effects__hint">
            { layer.asset ? 'Double-click the picture to move or zoom it inside the frame.' : 'Choose a picture in Uploads to fill this frame.' }
          </p>
        </section>
      ) }

      { outline && ! mask?.frame && (
        <p className="tb-effects__hint">
          Seen through { outline.type === 'text' ? `the text “${ ( outline as TextLayer ).text.slice( 0, 30 ) }”` : outline.type === 'graphic' ? 'a drawing' : `a ${ shapeDef( ( outline as { shape: string } ).shape ).name.toLowerCase() }` }.
          Double-click the picture to move or zoom it inside.
        </p>
      ) }

      { ! outline && (
        <p className="tb-effects__hint">
          To see this picture through a shape, a text or a drawing, select both and choose <strong>Use as mask</strong>. Or add a frame from Elements.
        </p>
      ) }

      <section className="tb-props__section">
        <h3 className="tb-props__heading">Soft edges</h3>
        <Slider label="Softness" value={ mask?.feather ?? 0 } onChange={ ( v ) => setMask( layer, { feather: v }, 'Soft edges' ) } />
      </section>

      <section className="tb-props__section">
        <h3 className="tb-props__heading">Fade</h3>
        <div className="tb-fill__tabs" role="tablist">
          { ( [ [ null, 'None' ], [ 'linear', 'Straight' ], [ 'radial', 'From the middle' ] ] as const ).map( ( [ kind, label ] ) => (
            <button
              key={ label }
              type="button"
              role="tab"
              className={ `tb-fill__tab${ ( fade?.kind ?? null ) === kind ? ' is-on' : '' }` }
              aria-selected={ ( fade?.kind ?? null ) === kind }
              onClick={ () => setFade( layer, kind ? { kind, angle: fade?.angle ?? 0, amount: fade?.amount || 50 } : null ) }
            >
              { label }
            </button>
          ) ) }
        </div>

        { fade && (
          <>
            <Slider label="How much fades" value={ fade.amount } onChange={ ( v ) => setFade( layer, { ...fade, amount: v } ) } />
            { fade.kind === 'linear' && (
              <Slider label="Direction (°)" value={ fade.angle } min={ 0 } max={ 359 } onChange={ ( v ) => setFade( layer, { ...fade, angle: v } ) } />
            ) }
          </>
        ) }
      </section>

      { ( ( mask?.feather ?? 0 ) > 0 || ( fade?.amount ?? 0 ) > 0 ) && (
        <p className="tb-effects__hint">Soft edges and fades print as transparency, which the press file flattens for you.</p>
      ) }

      { outline && (
        <button type="button" className="tb-btn tb-rp__wide" onClick={ () => releaseMask( layer.id ) }>
          { mask?.frame ? 'Take the shape out of the frame' : 'Release mask' }
        </button>
      ) }
    </div>
  );
}

/**
 * A picture inside a text's letters: which one, and how it fits.
 *
 * Shown under the text's colour, because it replaces the colour.
 */
export function TextPicture( { layer }: { layer: TextLayer } ) {
  const pictures = allAssets();
  const picture = layer.picture ?? null;

  const choose = ( id: string ) => {
    const asset = pictures.find( ( a ) => a.id === id );

    if ( ! asset ) return;

    useEditor.getState().commit( 'Picture in text', ( doc ) => {
      const store = useEditor.getState();
      const pages = doc.pages.map( ( page, index ) =>
        index !== store.pageIndex
          ? page
          : {
              ...page,
              layers: replace( page.layers, layer.id, ( l ) =>
                l.type === 'text' ? { ...l, picture: { asset: id, fit: picture?.fit ?? 'cover', crop: { x: 0, y: 0, w: 1, h: 1 } } } : l
              ),
            }
      );
      const known = doc.assets.some( ( ref ) => ref.id === id );

      return { ...doc, pages, assets: known ? doc.assets : doc.assets.concat( toDocumentRef( asset ) ) };
    } );
  };

  const update = ( next: TextLayer[ 'picture' ], label: string ) =>
    useEditor.getState().updateLayer( layer.id, label, ( l ) => ( { ...( l as TextLayer ), picture: next } ) );

  return (
    <section className="tb-props__section tb-text-picture">
      <h3 className="tb-props__heading">Picture inside the letters</h3>

      { pictures.length === 0 ? (
        <p className="tb-effects__hint">Upload a picture first, then choose it here.</p>
      ) : (
        <div className="tb-rp__pictures">
          { pictures.map( ( asset ) => (
            <button
              key={ asset.id }
              type="button"
              className={ `tb-rp__picture${ picture?.asset === asset.id ? ' is-on' : '' }` }
              title={ asset.name }
              aria-pressed={ picture?.asset === asset.id }
              onClick={ () => choose( asset.id ) }
            >
              <img src={ asset.url } alt={ asset.name } loading="lazy" />
            </button>
          ) ) }
        </div>
      ) }

      { picture && (
        <>
          <label className="tb-field">
            <span className="tb-field__label">Fit</span>
            <select
              className="tb-input"
              value={ picture.fit }
              onChange={ ( e ) => update( { ...picture, fit: e.target.value as 'cover' | 'contain' | 'fill' }, 'Picture fit' ) }
            >
              <option value="cover">Fill the text</option>
              <option value="contain">Show all of it</option>
              <option value="fill">Stretch</option>
            </select>
          </label>
          <button type="button" className="tb-btn tb-rp__wide" onClick={ () => update( null, 'Remove picture' ) }>
            Use the colour instead
          </button>
        </>
      ) }
    </section>
  );
}
